import { createContext, useContext, useState, ReactNode } from 'react';
import { AlertSnackbar } from '../Components/AlertSnackbar/AlertSnackbar';

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success';

interface AlertContextType {
  showAlert: (message: string, severity?: AlertSeverity) => void;
  hideAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider = ({ children }: AlertProviderProps) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertSeverity>('info');

  const showAlert = (msg: string, sev: AlertSeverity = 'info') => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  };

  const hideAlert = () => {
    setOpen(false);
  };

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert }}>
      {children}
      <AlertSnackbar open={open} message={message} severity={severity} onClose={hideAlert} />
    </AlertContext.Provider>
  );
};
